import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../apiClient'; // <-- Your API Client
import { Link } from 'react-router-dom';

// --- MUI Components ---
import {
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert, // Keep Alert for page-load errors
  Box, // Use Box for layout instead of Container
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar // 1. ✅ Import Snackbar for action feedback
} from '@mui/material';

// --- MUI Icons ---
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';

// A helper function to format the date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export default function BlogList() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null); // For page-load errors

  // --- State for Delete Confirmation Modal ---
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedBlogId, setSelectedBlogId] = useState(null);

  // 2. ✅ State for Snackbar feedback
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success', // 'success' or 'error'
  });

  // --- Data Fetching ---
  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const response = await apiClient.get('/blogs'); 
      setBlogs(response.data);
    } catch (err) {
      console.error('Error fetching blogs:', err);
      let errorMsg = 'Failed to fetch blogs. Please try again later.';
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        errorMsg = 'You are not authorized. Please log out and log in again.';
      }
      setPageError(errorMsg); // Use pageError for load failures
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  // --- Delete Logic ---
  const handleOpenDeleteDialog = (id) => {
    setSelectedBlogId(id);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedBlogId(null);
    setOpenDeleteDialog(false);
  };

  const handleDelete = async () => {
    if (!selectedBlogId) return;

    try {
      await apiClient.delete(`/blogs/${selectedBlogId}`);
      setBlogs(prevBlogs => prevBlogs.filter(blog => blog._id !== selectedBlogId));
      // 3. ✅ Show SUCCESS snackbar
      setSnackbar({ open: true, message: 'Blog deleted successfully', severity: 'success' });

    } catch (err) {
      console.error('Error deleting blog:', err);
      // 4. ✅ Show ERROR snackbar
      setSnackbar({ open: true, message: 'Failed to delete blog. Please try again.', severity: 'error' });
    } finally {
      handleCloseDeleteDialog();
    }
  };

  // 5. ✅ Handler to close the snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // --- Render Loading State ---
  if (loading) {
    return (
      // Use Box, centered in the main-content area
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // --- Render Error State ---
  if (pageError) {
    return (
      // Use Alert directly, it will be padded by the parent
      <Alert severity="error" sx={{ mt: 4 }}>{pageError}</Alert>
    );
  }

  // --- Main Render ---
  return (
    // 6. ✅ Use <Box> instead of <Container> to fit cleanly in AdminLayout
    <Box>
      {/* 1. Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Manage Blogs
        </Typography>
        <Button
          variant="contained"
          color="primary"
          component={Link}
          to="/admin/blog/new" 
          startIcon={<AddIcon />}
        >
          Add New Blog
        </Button>
      </Box>

      {/* 2. The Data Table */}
      <TableContainer component={Paper} sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <Table sx={{ minWidth: 650 }} aria-label="blogs table">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: '600', backgroundColor: '#f9fafb' } }}>
              {/* 7. ✅ ADDED Image column */}
              <TableCell sx={{ width: 120 }}>Image</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Website</TableCell>
              <TableCell>Category</TableCell>
              {/* 8. ✅ ADDED Date column */}
              <TableCell>Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {blogs.length === 0 ? (
              <TableRow>
                {/* 9. ✅ Update colSpan to 6 */}
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  No blogs found.
                </TableCell>
              </TableRow>
            ) : (
              blogs.map((blog) => (
                <TableRow
                  key={blog._id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { backgroundColor: '#f9fafb' } }}
                >
                  {/* 10. ✅ RENDER Image */}
                  <TableCell>
                    <Box
                      component="img"
                      src={blog.imageUrl}
                      alt={blog.title}
                      sx={{
                        height: 50,
                        width: 80,
                        objectFit: 'cover',
                        borderRadius: '4px',
                        border: '1px solid #eee'
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{blog.title}</TableCell>
                  <TableCell>{blog.website}</TableCell>
                  <TableCell>{blog.category}</TableCell>
                  {/* 11. ✅ RENDER Date */}
                  <TableCell>{formatDate(blog.date)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      component={Link}
                      to={`/admin/blog/edit/${blog._id}`}
                      color="primary"
                      aria-label="edit blog"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleOpenDeleteDialog(blog._id)}
                      aria-label="delete blog"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 3. Delete Confirmation Modal (Unchanged) */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this blog post? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 4. ✅ Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {/* We put an Alert inside the Snackbar for correct styling */}
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}